import React from "react";
import "./AnalysisDashboard.css";

function AnalysisDashboard({ isVisible, onClose, analysisData }) {
  if (!analysisData) return null;

  const {
    developmentSuitability = {},
    dashboardSummary = {},
    risks = [],
    recommendations = [],
    metrics = {},
    dataCompleteness = {},
    keyFindings = [],
  } = analysisData;

  // Helper function to get severity color
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return "#dc3545";
      case "high":
        return "#fd7e14";
      case "medium":
        return "#ffc107";
      case "low":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  // Helper function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "#dc3545";
      case "high":
        return "#fd7e14";
      case "medium":
        return "#ffc107";
      case "low":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  // Helper function to get score color
  const getScoreColor = (score) => {
    if (score >= 8) return "#28a745"; // Excellent - Green
    if (score >= 6) return "#ffc107"; // Good - Yellow
    if (score >= 4) return "#fd7e14"; // Fair - Orange
    return "#dc3545"; // Poor - Red
  };

  return (
    <div className={`dashboard-overlay ${isVisible ? "" : "hidden"}`}>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h2>📊 Analysis Dashboard</h2>
          <button className="dashboard-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="dashboard-body">
          {/* Development Suitability Section */}
          <section className="dashboard-section">
            <h3>🏗️ Development Suitability</h3>
            <div className="suitability-card">
              <div className="score-circle-container">
                <svg className="score-circle" viewBox="0 0 100 100">
                  <circle
                    className="score-bg"
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#e9ecef"
                    strokeWidth="8"
                  />
                  <circle
                    className="score-progress"
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke={getScoreColor(developmentSuitability.score || 0)}
                    strokeWidth="8"
                    strokeDasharray={`${
                      ((developmentSuitability.score || 0) / 10) * 283
                    } 283`}
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="score-text">
                  <div className="score-value">
                    {developmentSuitability.score || "N/A"}
                    <span className="score-max">/10</span>
                  </div>
                  <div className="score-level">
                    {developmentSuitability.level || "N/A"}
                  </div>
                </div>
              </div>
              <p className="score-summary">
                {developmentSuitability.summary || "No summary available"}
              </p>
            </div>
          </section>

          {/* Dashboard Summary */}
          <section className="dashboard-section">
            <h3>🎯 Overall Summary</h3>
            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-icon">🎯</div>
                <div className="summary-value">
                  {dashboardSummary.overallReadiness || 0}%
                </div>
                <div className="summary-label">Overall Readiness</div>
              </div>
              <div className="summary-card alert">
                <div className="summary-icon">⚠️</div>
                <div className="summary-value">
                  {dashboardSummary.criticalIssues || 0}
                </div>
                <div className="summary-label">Critical Issues</div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">📋</div>
                <div className="summary-value">
                  {dashboardSummary.actionItemsNeeded || 0}
                </div>
                <div className="summary-label">Action Items</div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">📊</div>
                <div className="summary-value">
                  {dataCompleteness.overall || 0}%
                </div>
                <div className="summary-label">Data Completeness</div>
              </div>
            </div>
          </section>

          {/* Risks Section */}
          {risks.length > 0 && (
            <section className="dashboard-section">
              <h3>🚨 Risk Analysis</h3>
              <div className="risks-list">
                {risks.slice(0, 5).map((risk, idx) => (
                  <div key={idx} className="risk-card">
                    <div className="risk-header">
                      <span
                        className="risk-severity-badge"
                        style={{
                          backgroundColor: getSeverityColor(risk.severity),
                        }}
                      >
                        {risk.severity || "Unknown"}
                      </span>
                      <h4>{risk.category || "Unknown Risk"}</h4>
                    </div>
                    <p className="risk-impact">{risk.impact || "No details"}</p>
                    <div className="risk-meta">
                      <span>
                        📊 Probability: {risk.probability || "Unknown"}
                      </span>
                      <span>⏰ {risk.timeframe || "Unknown"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recommendations Section */}
          {recommendations.length > 0 && (
            <section className="dashboard-section">
              <h3>💡 Recommendations</h3>
              <div className="recommendations-list">
                {recommendations.slice(0, 5).map((rec, idx) => (
                  <div key={idx} className="recommendation-card">
                    <div className="rec-header">
                      <span
                        className="rec-priority-badge"
                        style={{
                          backgroundColor: getPriorityColor(rec.priority),
                        }}
                      >
                        {rec.priority || "N/A"}
                      </span>
                      <div className="rec-meta">
                        <span className="rec-cost">{rec.cost || "N/A"}</span>
                        <span className="rec-effort">
                          Effort: {rec.effort || "N/A"}
                        </span>
                      </div>
                    </div>
                    <p className="rec-action">{rec.action || "No action"}</p>
                    <p className="rec-impact">
                      <strong>Impact:</strong> {rec.impact || "Unknown"}
                    </p>
                    <p className="rec-timeframe">
                      <strong>Timeframe:</strong> {rec.timeframe || "Unknown"}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Environmental Metrics */}
          {metrics && (
            <section className="dashboard-section">
              <h3>🌡️ Environmental Metrics</h3>
              <div className="metrics-grid">
                {metrics.temperature && (
                  <div className="metric-card">
                    <div className="metric-icon">🌡️</div>
                    <h4>Temperature</h4>
                    <div className="metric-value">
                      {metrics.temperature.current?.toFixed(1) || "N/A"}°C
                    </div>
                    <div className="metric-range">
                      Min: {metrics.temperature.min?.toFixed(1) || "N/A"}°C |
                      Max: {metrics.temperature.max?.toFixed(1) || "N/A"}°C
                    </div>
                    <div className="metric-trend">
                      Trend: {metrics.temperature.trend || "Unknown"}
                    </div>
                  </div>
                )}

                {metrics.precipitation && (
                  <div className="metric-card">
                    <div className="metric-icon">🌧️</div>
                    <h4>Precipitation</h4>
                    <div className="metric-value">
                      {metrics.precipitation.total?.toFixed(1) || "N/A"} mm
                    </div>
                    <div className="metric-range">
                      Avg: {metrics.precipitation.average?.toFixed(2) || "N/A"}{" "}
                      mm/day
                    </div>
                    <div className="metric-trend">
                      Trend: {metrics.precipitation.trend || "Unknown"}
                    </div>
                  </div>
                )}

                {metrics.humidity && (
                  <div className="metric-card">
                    <div className="metric-icon">💧</div>
                    <h4>Humidity</h4>
                    <div className="metric-value">
                      {metrics.humidity.average?.toFixed(1) || "N/A"}%
                    </div>
                    <div className="metric-assessment">
                      {metrics.humidity.assessment || "Unknown"}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Key Findings */}
          {keyFindings.length > 0 && (
            <section className="dashboard-section">
              <h3>🔍 Key Findings</h3>
              <div className="findings-list">
                {keyFindings.map((finding, idx) => (
                  <div key={idx} className="finding-card">
                    <div className="finding-header">
                      <span className="finding-category">
                        {finding.category || "General"}
                      </span>
                      <span className="finding-confidence">
                        Confidence:{" "}
                        {((finding.confidence || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="finding-text">
                      {finding.finding || "No finding"}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalysisDashboard;
